import React from "react";
import { Link } from "react-router-dom";

/**
 * AllinstraLogo
 *
 * - "icon"      → just the A-mark (mobile, favicon-like spaces, compact UI).
 * - "full"      → full wordmark (icon + Allinstra · Institutional Systems). Default.
 * - "dark"      → wordmark renders against light backgrounds (default).
 * - "light"     → wordmark renders against dark backgrounds (footer / dark sections).
 *                  In light variant the wordmark text is rendered in white from a sibling
 *                  span so the brand reads correctly on dark navy.
 */
export const AllinstraLogo = ({
  variant = "dark",
  layout = "full",
  size = 32,
  className = "",
}) => {
  const isLight = variant === "light";
  const iconSrc = "/brand/allinstra-logo-icon-transparent.png";
  const fullSrc = "/brand/allinstra-logo-full.png";

  // Use the supplied image directly. For the dark variant it sits on a white
  // background; for the light variant we keep the same vivid blue mark since the
  // mark is already cyan/blue and reads cleanly on dark navy. The wordmark for
  // the light variant is rendered as text so it stays crisp in white.
  if (layout === "icon") {
    return (
      <Link
        to="/"
        data-testid="brand-logo-link"
        className={`inline-flex items-center group ${className}`}
        aria-label="Allinstra — Home"
      >
        <img
          src={iconSrc}
          alt="Allinstra"
          width={size}
          height={size}
          className="block"
          style={{ width: size, height: size }}
        />
      </Link>
    );
  }

  // Full layout — icon + wordmark
  // For the dark variant we render the full transparent PNG (which already
  // contains "Allinstra · Institutional Systems") so the white pixels become
  // see-through on dark navy, keeping the brand crisp against any background.
  if (isLight) {
    return (
      <Link
        to="/"
        data-testid="brand-logo-link"
        className={`inline-flex items-center group ${className}`}
        aria-label="Allinstra — Home"
      >
        <img
          src="/brand/allinstra-logo-full.png"
          alt="Allinstra · Institutional Systems"
          className="block"
          style={{ height: size + 8, width: "auto" }}
        />
      </Link>
    );
  }

  return (
    <Link
      to="/"
      data-testid="brand-logo-link"
      className={`inline-flex items-center group ${className}`}
      aria-label="Allinstra — Home"
    >
      <img
        src={fullSrc}
        alt="Allinstra · Institutional Systems"
        className="block"
        style={{ height: size + 8, width: "auto" }}
      />
    </Link>
  );
};

export default AllinstraLogo;
